import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Package, RefreshCw, Search, ChevronLeft, ChevronRight, Clock, AlertCircle, 
  Filter, X, CheckCircle, Inbox, Truck, XCircle, Copy, MapPin, ExternalLink, Send, RotateCcw,
  Download
} from "lucide-react";

const PharmeasyOrders = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [lastSync, setLastSync] = useState(null);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [showFilters, setShowFilters] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ cities: [], pincodes: [], payment_types: [], priority_types: [], bolt_statuses: [], our_statuses: [], shipment_types: [], ofd_counts: [], unattempted_counts: [] });
  const [fetchingEventsFor, setFetchingEventsFor] = useState(null);
  const [bulkFetchingEvents, setBulkFetchingEvents] = useState(false);
  const [bulkFetchProgress, setBulkFetchProgress] = useState(null); // { status, total, processed, success, failed, delivered }
  const bulkPollRef = useRef(null);
  const [resettingStatus, setResettingStatus] = useState(false);
  
  // Filter state - default to UNASSIGNED tab
  const [filters, setFilters] = useState({
    status: "UNASSIGNED",
    bolt_status: "",
    payment_type: "",
    priority: "",
    drop_city: "",
    drop_pincode: "",
    detail_fetched: "",
    shipment_type: "",  // NEW: Filter by type (RETURN, FORWARD, DELIVERY)
    present_in_last_fetch: "",
    // Additional dropdown filters (independent of tab selection)
    filter_bolt_status: "",
    filter_our_status: "",
    ofd_count: "",
    unattempted_count: "",
  });
  
  const pageSize = 50;

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, size: pageSize });
      if (filters.status) params.set("status", filters.status);
      if (filters.bolt_status) params.set("bolt_status", filters.bolt_status);
      if (filters.payment_type) params.set("payment_type", filters.payment_type);
      if (filters.priority) params.set("priority", filters.priority);
      if (filters.drop_city) params.set("drop_city", filters.drop_city);
      if (filters.drop_pincode) params.set("drop_pincode", filters.drop_pincode);
      if (filters.detail_fetched) params.set("detail_fetched", filters.detail_fetched);
      if (filters.shipment_type) params.set("shipment_type", filters.shipment_type);
      if (filters.present_in_last_fetch) params.set("present_in_last_fetch", filters.present_in_last_fetch);
      if (filters.filter_bolt_status) params.set("filter_bolt_status", filters.filter_bolt_status);
      if (filters.filter_our_status) params.set("filter_our_status", filters.filter_our_status);
      if (filters.ofd_count !== "") params.set("ofd_count", filters.ofd_count);
      if (filters.unattempted_count !== "") params.set("unattempted_count", filters.unattempted_count);
      if (searchQuery.trim()) params.set("search", searchQuery.trim());
      
      const res = await axios.get(`${API}/pharmeasy-orders/shipments?${params}`);
      setShipments(res.data.data);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (e) { toast.error("Failed to fetch shipments"); }
    finally { setLoading(false); }
  }, [page, searchQuery, filters]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-orders/stats`);
      setStats(res.data);
    } catch (e) { /* silent */ }
  };

  const fetchLastSync = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-orders/last-sync`);
      setLastSync(res.data);
    } catch (e) { /* silent */ }
  };

  const fetchFilterOptions = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-orders/filter-options`);
      setFilterOptions(res.data);
    } catch (e) { /* silent */ }
  };

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.bolt_status) params.set("bolt_status", filters.bolt_status);
      if (filters.drop_city) params.set("drop_city", filters.drop_city);
      if (filters.drop_pincode) params.set("drop_pincode", filters.drop_pincode);
      if (filters.detail_fetched) params.set("detail_fetched", filters.detail_fetched);
      if (filters.shipment_type) params.set("shipment_type", filters.shipment_type);
      if (filters.present_in_last_fetch) params.set("present_in_last_fetch", filters.present_in_last_fetch);
      if (filters.filter_bolt_status) params.set("filter_bolt_status", filters.filter_bolt_status);
      if (filters.filter_our_status) params.set("filter_our_status", filters.filter_our_status);
      if (filters.ofd_count !== "") params.set("ofd_count", filters.ofd_count);
      if (filters.unattempted_count !== "") params.set("unattempted_count", filters.unattempted_count);
      if (filters.payment_type) params.set("payment_type", filters.payment_type);
      if (filters.priority) params.set("priority", filters.priority);
      if (searchQuery.trim()) params.set("search", searchQuery.trim());
      
      const res = await axios.get(`${API}/pharmeasy-orders/shipments-export-csv?${params}`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const section = filters.status || filters.bolt_status || "all";
      a.download = `shipments_${section}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV exported successfully");
    } catch (e) {
      toast.error("Failed to export CSV");
    }
  };

  useEffect(() => { fetchShipments(); }, [fetchShipments]);
  useEffect(() => { fetchStats(); fetchLastSync(); fetchFilterOptions(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/sync-unassigned`);
      const d = res.data;
      toast.success(d.message || "Sync started in background");
      setPage(1);
      fetchShipments();
      fetchStats();
      fetchLastSync();
      fetchFilterOptions();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "object") toast.error(detail.error || "Sync failed");
      else toast.error(detail || "Sync failed");
    } finally { setSyncing(false); }
  };

  const handleFetchEvents = async (shipmentId, lbn) => {
    if (!lbn) {
      toast.error("Shipment has no LBN - cannot fetch events");
      return;
    }
    setFetchingEventsFor(shipmentId);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/shipments/${shipmentId}/fetch-events`);
      toast.success(`Status updated: ${res.data.latest_status}`);
      // Refresh the shipments list to show updated status
      fetchShipments();
      fetchStats();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Failed to fetch events");
    } finally {
      setFetchingEventsFor(null);
    }
  };

  // Per-shipment reset to unassigned
  const [resettingSingle, setResettingSingle] = useState(null);
  const handleResetSingle = async (shipmentId) => {
    if (!window.confirm(`Reset shipment ${shipmentId} to UNASSIGNED?`)) return;
    setResettingSingle(shipmentId);
    try {
      await axios.post(`${API}/pharmeasy-orders/shipments/${shipmentId}/reset-to-unassigned`);
      toast.success(`Shipment ${shipmentId} reset to UNASSIGNED`);
      fetchShipments();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset");
    } finally {
      setResettingSingle(null);
    }
  };

  // Tabs where bulk fetch events is allowed
  const bulkFetchAllowedTabs = ["UNASSIGNED", "INWARDED", "INWARD_PLANNED", "TRIP_CREATED_RECEIVED", "FORWARD_DISPATCHED", "UNDELIVERED_RETRY", "OTHERS"];
  const isBulkFetchVisible = bulkFetchAllowedTabs.includes(filters.status) || ["OUT_FOR_DELIVERY", "PICKUP_COMPLETE"].includes(filters.bolt_status);
  const isBulkFetchInProgress = bulkFetchingEvents || bulkFetchProgress?.status === "in_progress";

  const startBulkFetchPolling = () => {
    if (bulkPollRef.current) clearInterval(bulkPollRef.current);
    bulkPollRef.current = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/pharmeasy-orders/bulk-fetch-events/status`);
        const data = res.data;
        setBulkFetchProgress(data);
        if (data.status === "completed" || data.status === "none") {
          clearInterval(bulkPollRef.current);
          bulkPollRef.current = null;
          setBulkFetchingEvents(false);
          if (data.status === "completed") {
            toast.success(`Bulk fetch complete: ${data.success} updated, ${data.failed} failed, ${data.delivered} delivered`);
            fetchShipments();
            fetchStats();
          }
        }
      } catch {
        // silently continue polling
      }
    }, 3000);
  };

  // Check for in-progress bulk fetch on mount
  useEffect(() => {
    const checkActiveJob = async () => {
      try {
        const res = await axios.get(`${API}/pharmeasy-orders/bulk-fetch-events/status`);
        if (res.data.status === "in_progress") {
          setBulkFetchProgress(res.data);
          setBulkFetchingEvents(true);
          startBulkFetchPolling();
        }
      } catch { /* silent */ }
    };
    checkActiveJob();
    return () => { if (bulkPollRef.current) clearInterval(bulkPollRef.current); };
  }, []); // eslint-disable-line

  const handleBulkFetchEvents = async () => {
    setBulkFetchingEvents(true);
    setBulkFetchProgress(null);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.set("status", filters.status);
      if (filters.bolt_status) params.set("bolt_status", filters.bolt_status);
      if (filters.payment_type) params.set("payment_type", filters.payment_type);
      if (filters.priority) params.set("priority", filters.priority);
      if (filters.drop_city) params.set("drop_city", filters.drop_city);
      if (filters.drop_pincode) params.set("drop_pincode", filters.drop_pincode);
      if (filters.detail_fetched) params.set("detail_fetched", filters.detail_fetched);
      if (filters.shipment_type) params.set("shipment_type", filters.shipment_type);
      if (filters.present_in_last_fetch) params.set("present_in_last_fetch", filters.present_in_last_fetch);
      if (filters.filter_bolt_status) params.set("filter_bolt_status", filters.filter_bolt_status);
      if (filters.filter_our_status) params.set("filter_our_status", filters.filter_our_status);
      if (searchQuery.trim()) params.set("search", searchQuery.trim());

      const res = await axios.post(`${API}/pharmeasy-orders/bulk-fetch-events?${params}`);
      if (res.data.status === "no_match") {
        toast.info(res.data.message);
        setBulkFetchingEvents(false);
      } else {
        toast.success(res.data.message);
        setBulkFetchProgress({ status: "in_progress", total: res.data.total, processed: 0, success: 0, failed: 0, delivered: 0 });
        startBulkFetchPolling();
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || "Failed to trigger bulk event fetch");
      setBulkFetchingEvents(false);
    }
  };

  const handleResetToUnassigned = async () => {
    const statusesToReset = [];
    let boltStatusFilter = "";
    if (filters.status === "INWARD_PLANNED") statusesToReset.push("INWARD_PLANNED");
    else if (filters.status === "INWARDED") statusesToReset.push("INWARDED");
    else if (filters.status === "UNDELIVERED_RETRY") {
      statusesToReset.push("INWARD_PLANNED");
      boltStatusFilter = "UNDELIVERED_RETRY";
    }
    else statusesToReset.push("INWARD_PLANNED", "INWARDED");

    const label = filters.status === "UNDELIVERED_RETRY"
      ? "INWARD_PLANNED (Postponed)"
      : statusesToReset.join(" & ");
    if (!window.confirm(`Reset all "${label}" orders to Unassigned? This cannot be undone.`)) return;

    setResettingStatus(true);
    try {
      let url = `${API}/pharmeasy-orders/reset-status-to-unassigned?statuses=${statusesToReset.join(",")}`;
      if (boltStatusFilter) url += `&bolt_status_filter=${boltStatusFilter}`;
      const res = await axios.post(url);
      toast.success(`${res.data.modified} orders reset to Unassigned`);
      fetchShipments();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset statuses");
    } finally {
      setResettingStatus(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchShipments();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: "",
      bolt_status: "",
      payment_type: "",
      priority: "",
      drop_city: "",
      drop_pincode: "",
      detail_fetched: "",
      filter_bolt_status: "",
      filter_our_status: "",
    });
    setSearchQuery("");
    setPage(1);
  };

  const handleStatusCardClick = (statusType, statusValue) => {
    // Clear both status filters first, then set the appropriate one
    if (statusType === "bolt_status") {
      setFilters(prev => ({ ...prev, status: "", bolt_status: statusValue }));
    } else {
      setFilters(prev => ({ ...prev, status: statusValue, bolt_status: "" }));
    }
    setPage(1);
  };

  const activeFilterCount = Object.entries(filters).filter(([k, v]) => v && k !== "status" && k !== "bolt_status").length + (searchQuery ? 1 : 0);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  // Get the relevant address based on shipment type
  // For RETURN: srcAddress is the pickup location (customer)
  // For FORWARD/DELIVERY: destAddress is the delivery location (customer)
  const getRelevantAddress = (s) => {
    if (s.type === "RETURN") {
      return s.srcAddress;
    }
    return s.destAddress;
  };

  const getFullAddress = (s) => {
    const parts = [];
    const addr = getRelevantAddress(s);
    
    if (addr) {
      if (addr.address1) parts.push(addr.address1);
      if (addr.address2) parts.push(addr.address2);
      if (addr.city) parts.push(addr.city);
      if (addr.pincode) parts.push(addr.pincode);
    } else {
      // Fallback to legacy fields
      if (s.dropAddressLine1) parts.push(s.dropAddressLine1);
      if (s.dropAddressLine2) parts.push(s.dropAddressLine2);
      if (s.dropCity) parts.push(s.dropCity);
      if (s.dropPincode) parts.push(s.dropPincode);
    }
    return parts.join(", ") || "—";
  };

  const getMapUrl = (s) => {
    // For RETURN type, use srcAddress coordinates
    if (s.type === "RETURN") {
      // Try calculated src coordinates first
      if (s.calculatedSrcAddressCoordinate?.coordinates) {
        const { lat, lng } = s.calculatedSrcAddressCoordinate.coordinates;
        return `https://www.google.com/maps?q=${lat},${lng}`;
      }
      // Try srcAddress coordinates
      if (s.srcAddress?.latitude && s.srcAddress?.longitude && 
          s.srcAddress.latitude !== "0.000000" && s.srcAddress.longitude !== "0.000000") {
        return `https://www.google.com/maps?q=${s.srcAddress.latitude},${s.srcAddress.longitude}`;
      }
    } else {
      // For FORWARD/DELIVERY, use destAddress coordinates
      if (s.calculatedDestAddressCoordinate?.coordinates) {
        const { lat, lng } = s.calculatedDestAddressCoordinate.coordinates;
        return `https://www.google.com/maps?q=${lat},${lng}`;
      }
      if (s.destAddress?.latitude && s.destAddress?.longitude &&
          s.destAddress.latitude !== "0.000000" && s.destAddress.longitude !== "0.000000") {
        return `https://www.google.com/maps?q=${s.destAddress.latitude},${s.destAddress.longitude}`;
      }
    }
    // Fallback to address search
    const addr = getFullAddress(s);
    if (addr && addr !== "—") {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`;
    }
    return null;
  };

  // Check if EDD has passed and order is not in terminal status
  const isEDDBreached = (s) => {
    if (!s.committedExpectedDeliveryDate) return false;
    // Terminal statuses - don't highlight these
    const terminalStatuses = ["DELIVERED", "CANCELLED", "RTO_DELIVERED", "RETURNED"];
    if (terminalStatuses.includes(s.bolt_status?.toUpperCase())) return false;
    
    const edd = new Date(s.committedExpectedDeliveryDate);
    const now = new Date();
    return now > edd;
  };

  const handleShipmentClick = (mongoId) => {
    navigate(`/pharmeasy/orders/${mongoId}`);
  };

  // Clickable cell component
  const ClickableCell = ({ value, mongoId, className = "" }) => (
    <span 
      onClick={() => handleShipmentClick(mongoId)}
      className={`cursor-pointer hover:text-emerald-600 hover:underline ${className}`}
      data-testid={`clickable-${mongoId}`}
    >
      {value || "—"}
    </span>
  );

  // Status badge colors
  const getBoltStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "DELIVERED": return "bg-green-100 text-green-700";
      case "OUT_FOR_DELIVERY": return "bg-blue-100 text-blue-700";
      case "UNASSIGNED": return "bg-slate-100 text-slate-600";
      case "CANCELLED": case "RTO_DELIVERED": case "RETURNED": return "bg-red-100 text-red-700";
      case "POSTPONED": return "bg-orange-100 text-orange-700";
      default: return "bg-amber-100 text-amber-700";
    }
  };

  const getOurStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "DELIVERED": return "bg-green-100 text-green-700";
      case "INWARDED": return "bg-purple-100 text-purple-700";
      case "INWARD_PLANNED": return "bg-orange-100 text-orange-700";
      case "UNASSIGNED": return "bg-amber-100 text-amber-700";
      case "CANCELLED": case "RTO": case "FAILED": return "bg-red-100 text-red-700";
      default: return "bg-slate-100 text-slate-600";
    }
  };

  // Terminal statuses where we don't need to fetch events
  const isTerminalStatus = (boltStatus) => {
    return ["DELIVERED", "CANCELLED", "RTO_DELIVERED", "RETURNED"].includes(boltStatus?.toUpperCase());
  };

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="pharmeasy-orders">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-800 mb-2">Pharmeasy Orders</h1>
            <p className="text-slate-600">Manage shipments assigned for delivery</p>
          </div>
          <div className="flex items-center gap-4">
            {lastSync?.completed_at && (
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" /> Last sync: {formatDate(lastSync.completed_at)}
              </span>
            )}
            <Button onClick={handleSync} disabled={syncing} style={{ backgroundColor: "#1B8C3D" }} data-testid="sync-orders-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing..." : "Fetch Orders"}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-8 gap-4">
          <Card 
            className={`border-0 shadow-md bg-amber-50 cursor-pointer transition-all ${filters.status === "UNASSIGNED" ? "ring-2 ring-amber-500" : ""}`}
            onClick={() => handleStatusCardClick("status", "UNASSIGNED")}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-500" />
                <p className="text-xs font-medium text-amber-600 uppercase">Unassigned</p>
              </div>
              <p className="text-2xl font-bold text-amber-800 mt-1">{stats.by_status?.UNASSIGNED || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md bg-orange-50 cursor-pointer transition-all ${filters.status === "INWARD_PLANNED" ? "ring-2 ring-orange-500" : ""}`}
            onClick={() => handleStatusCardClick("status", "INWARD_PLANNED")}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-orange-500" />
                <p className="text-xs font-medium text-orange-600 uppercase">Inward Planned</p>
              </div>
              <p className="text-2xl font-bold text-orange-800 mt-1">{stats.by_status?.INWARD_PLANNED || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md bg-purple-50 cursor-pointer transition-all ${filters.status === "INWARDED" ? "ring-2 ring-purple-500" : ""}`}
            onClick={() => handleStatusCardClick("status", "INWARDED")}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2">
                <Inbox className="w-5 h-5 text-purple-500" />
                <p className="text-xs font-medium text-purple-600 uppercase">Inwarded</p>
              </div>
              <p className="text-2xl font-bold text-purple-800 mt-1">{stats.by_status?.INWARDED || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md bg-blue-50 cursor-pointer transition-all ${filters.bolt_status === "OUT_FOR_DELIVERY" ? "ring-2 ring-blue-500" : ""}`}
            onClick={() => handleStatusCardClick("bolt_status", "OUT_FOR_DELIVERY")}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-blue-500" />
                <p className="text-xs font-medium text-blue-600 uppercase">Out for Delivery</p>
              </div>
              <p className="text-2xl font-bold text-blue-800 mt-1">{stats.by_bolt_status?.OUT_FOR_DELIVERY || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md bg-amber-50 cursor-pointer transition-all ${filters.status === "UNDELIVERED_RETRY" ? "ring-2 ring-amber-500" : ""}`}
            onClick={() => handleStatusCardClick("status", "UNDELIVERED_RETRY")}
            data-testid="undelivered-retry-tab"
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-amber-500" />
                <p className="text-xs font-medium text-amber-600 uppercase">Postponed Task</p>
              </div>
              <p className="text-2xl font-bold text-amber-800 mt-1">{stats.by_bolt_status?.UNDELIVERED_RETRY || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md bg-emerald-50 cursor-pointer transition-all ${filters.bolt_status === "DELIVERED" ? "ring-2 ring-emerald-500" : ""}`}
            onClick={() => handleStatusCardClick("bolt_status", "DELIVERED")}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
                <p className="text-xs font-medium text-emerald-600 uppercase">Delivered</p>
              </div>
              <p className="text-2xl font-bold text-emerald-800 mt-1">{stats.by_bolt_status?.DELIVERED || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md bg-cyan-50 cursor-pointer transition-all ${filters.status === "FORWARD_DISPATCHED" ? "ring-2 ring-cyan-500" : ""}`}
            onClick={() => handleStatusCardClick("status", "FORWARD_DISPATCHED")}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2">
                <Send className="w-5 h-5 text-cyan-500" />
                <p className="text-xs font-medium text-cyan-600 uppercase">Forward Dispatched</p>
              </div>
              <p className="text-2xl font-bold text-cyan-800 mt-1">{stats.by_bolt_status?.FORWARD_DISPATCHED || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md bg-violet-50 cursor-pointer transition-all ${filters.bolt_status === "PICKUP_COMPLETE" ? "ring-2 ring-violet-500" : ""}`}
            onClick={() => handleStatusCardClick("bolt_status", "PICKUP_COMPLETE")}
            data-testid="pickup-complete-tab"
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-violet-500" />
                <p className="text-xs font-medium text-violet-600 uppercase">Pickup Complete</p>
              </div>
              <p className="text-2xl font-bold text-violet-800 mt-1">{stats.by_bolt_status?.PICKUP_COMPLETE || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md bg-teal-50 cursor-pointer transition-all ${filters.status === "RTO_COMPLETED" ? "ring-2 ring-teal-500" : ""}`}
            onClick={() => handleStatusCardClick("status", "RTO_COMPLETED")}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-teal-500" />
                <p className="text-xs font-medium text-teal-600 uppercase">RTO Completed</p>
              </div>
              <p className="text-2xl font-bold text-teal-800 mt-1">{stats.by_bolt_status?.RTO_COMPLETED || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md bg-red-50 cursor-pointer transition-all ${filters.bolt_status === "CANCELLED" ? "ring-2 ring-red-500" : ""}`}
            onClick={() => handleStatusCardClick("bolt_status", "CANCELLED")}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2">
                <XCircle className="w-5 h-5 text-red-500" />
                <p className="text-xs font-medium text-red-600 uppercase">Cancelled</p>
              </div>
              <p className="text-2xl font-bold text-red-800 mt-1">{stats.by_bolt_status?.CANCELLED || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md bg-indigo-50 cursor-pointer transition-all ${filters.status === "TRIP_CREATED_RECEIVED" ? "ring-2 ring-indigo-500" : ""}`}
            onClick={() => handleStatusCardClick("status", "TRIP_CREATED_RECEIVED")}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2">
                <Truck className="w-5 h-5 text-indigo-500" />
                <p className="text-xs font-medium text-indigo-600 uppercase">Received</p>
              </div>
              <p className="text-2xl font-bold text-indigo-800 mt-1">{stats.by_status?.TRIP_CREATED_RECEIVED || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md bg-gray-100 cursor-pointer transition-all ${filters.status === "OTHERS" ? "ring-2 ring-gray-500" : ""}`}
            onClick={() => handleStatusCardClick("status", "OTHERS")}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-gray-500" />
                <p className="text-xs font-medium text-gray-600 uppercase">Others</p>
              </div>
              <p className="text-2xl font-bold text-gray-800 mt-1">{stats.by_status?.OTHERS || 0}</p>
            </CardContent>
          </Card>
          <Card 
            className={`border-0 shadow-md cursor-pointer transition-all ${!filters.status && !filters.bolt_status ? "ring-2 ring-slate-400" : ""}`}
            onClick={() => { setFilters(prev => ({ ...prev, status: "", bolt_status: "" })); setPage(1); }}
          >
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-slate-400" />
                <p className="text-xs font-medium text-slate-500 uppercase">Total</p>
              </div>
              <p className="text-2xl font-bold text-slate-800 mt-1">{stats.total}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters & Search */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-5">
            <div className="flex flex-col gap-4">
              {/* Top row: Status Badge, Search, Filter Toggle */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex gap-3 items-center">
                  <Badge className={`px-3 py-1.5 text-sm cursor-default ${
                    filters.status === "UNASSIGNED" ? "bg-amber-100 text-amber-800" :
                    filters.status === "INWARD_PLANNED" ? "bg-orange-100 text-orange-800" :
                    filters.status === "INWARDED" ? "bg-purple-100 text-purple-800" :
                    filters.status === "TRIP_CREATED_RECEIVED" ? "bg-indigo-100 text-indigo-800" :
                    filters.status === "FORWARD_DISPATCHED" ? "bg-cyan-100 text-cyan-800" :
                    filters.status === "RETURN_DISPATCHED" ? "bg-teal-100 text-teal-800" :
                    filters.status === "RTO_COMPLETED" ? "bg-teal-100 text-teal-800" :
                    filters.status === "OTHERS" ? "bg-gray-200 text-gray-800" :
                    filters.bolt_status === "OUT_FOR_DELIVERY" ? "bg-blue-100 text-blue-800" :
                    filters.bolt_status === "DELIVERED" ? "bg-emerald-100 text-emerald-800" :
                    filters.bolt_status === "CANCELLED" ? "bg-red-100 text-red-800" :
                    "bg-slate-100 text-slate-800"
                  }`}>
                    {filters.bolt_status === "OUT_FOR_DELIVERY" ? "Out for Delivery" : 
                     filters.bolt_status === "DELIVERED" ? "Delivered" :
                     filters.bolt_status === "CANCELLED" ? "Cancelled" :
                     filters.status === "TRIP_CREATED_RECEIVED" ? "Received" :
                     filters.status === "INWARD_PLANNED" ? "Inward Planned" :
                     filters.status === "FORWARD_DISPATCHED" ? "Forward Dispatched" :
                     filters.status === "UNDELIVERED_RETRY" ? "Postponed Task" :
                     filters.status === "RETURN_DISPATCHED" ? "Return Dispatched" :
                     filters.status === "RTO_COMPLETED" ? "RTO Completed" :
                     filters.status === "OTHERS" ? "Others" :
                     (filters.status || "All")} ({total})
                  </Badge>
                  {activeFilterCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="text-slate-500 hover:text-red-600">
                      <X className="w-4 h-4 mr-1" /> Clear ({activeFilterCount})
                    </Button>
                  )}
                </div>
                <div className="flex flex-wrap gap-3 items-center">
                  <form onSubmit={handleSearch} className="flex gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input placeholder="Search ID, reference, LBN, pincode, packageId..."
                        value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-10 w-80" data-testid="shipment-search-input" />
                    </div>
                    <Button type="submit" variant="outline" data-testid="shipment-search-btn">Search</Button>
                  </form>
                  <Button 
                    variant="outline" 
                    onClick={() => setShowFilters(!showFilters)}
                    className={showFilters ? "bg-slate-100" : ""}
                    data-testid="toggle-filters-btn"
                  >
                    <Filter className="w-4 h-4 mr-2" /> Filters
                  </Button>
                  <Button
                    variant="outline"
                    onClick={exportCSV}
                    data-testid="export-csv-btn"
                    className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                  >
                    <Download className="w-4 h-4 mr-2" /> Export CSV
                  </Button>
                  {isBulkFetchVisible && (
                    <Button
                      variant="outline"
                      onClick={handleBulkFetchEvents}
                      disabled={isBulkFetchInProgress}
                      data-testid="bulk-fetch-events-btn"
                      className={`border-blue-200 ${isBulkFetchInProgress ? "bg-blue-50 text-blue-500 cursor-not-allowed" : "text-blue-600 hover:text-blue-700 hover:bg-blue-50"}`}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${isBulkFetchInProgress ? "animate-spin" : ""}`} />
                      {isBulkFetchInProgress && bulkFetchProgress
                        ? `${bulkFetchProgress.processed}/${bulkFetchProgress.total} fetched`
                        : "Bulk Fetch Events"}
                    </Button>
                  )}
                  {(filters.status === "INWARD_PLANNED" || filters.status === "INWARDED" || filters.status === "UNDELIVERED_RETRY") && (
                    <Button
                      variant="outline"
                      onClick={handleResetToUnassigned}
                      disabled={resettingStatus}
                      data-testid="reset-to-unassigned-btn"
                      className="border-amber-200 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                    >
                      <RotateCcw className={`w-4 h-4 mr-2 ${resettingStatus ? "animate-spin" : ""}`} />
                      {resettingStatus ? "Resetting..." : "Reset to Unassigned"}
                    </Button>
                  )}
                </div>
              </div>

              {/* Bulk Fetch Progress Bar */}
              {isBulkFetchInProgress && bulkFetchProgress && (
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200" data-testid="bulk-fetch-progress">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-700 flex items-center gap-2">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Bulk fetching events... {bulkFetchProgress.processed}/{bulkFetchProgress.total}
                    </span>
                    <span className="text-xs text-blue-500">
                      {bulkFetchProgress.success} updated, {bulkFetchProgress.failed} failed, {bulkFetchProgress.delivered} delivered
                    </span>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${bulkFetchProgress.total ? Math.round((bulkFetchProgress.processed / bulkFetchProgress.total) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Expanded Filters */}
              {showFilters && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 p-4 bg-slate-50 rounded-lg" data-testid="filter-panel">
                  <div>
                    <Label className="text-xs text-orange-600 font-semibold">Pharmeasy Status</Label>
                    <select
                      value={filters.filter_bolt_status}
                      onChange={(e) => handleFilterChange("filter_bolt_status", e.target.value)}
                      className="w-full mt-1 text-sm border rounded-md px-3 py-2 bg-white"
                      data-testid="filter-pharmeasy-status"
                    >
                      <option value="">All</option>
                      {filterOptions.bolt_statuses.map(s => (
                        <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-purple-600 font-semibold">Edgistify Status</Label>
                    <select
                      value={filters.filter_our_status}
                      onChange={(e) => handleFilterChange("filter_our_status", e.target.value)}
                      className="w-full mt-1 text-sm border rounded-md px-3 py-2 bg-white"
                      data-testid="filter-edgistify-status"
                    >
                      <option value="">All</option>
                      {filterOptions.our_statuses.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Payment Type</Label>
                    <select
                      value={filters.payment_type}
                      onChange={(e) => handleFilterChange("payment_type", e.target.value)}
                      className="w-full mt-1 text-sm border rounded-md px-3 py-2 bg-white"
                      data-testid="filter-payment-type"
                    >
                      <option value="">All</option>
                      {filterOptions.payment_types.map(pt => (
                        <option key={pt} value={pt}>{pt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Type</Label>
                    <select
                      value={filters.shipment_type}
                      onChange={(e) => handleFilterChange("shipment_type", e.target.value)}
                      className="w-full mt-1 text-sm border rounded-md px-3 py-2 bg-white"
                      data-testid="filter-shipment-type"
                    >
                      <option value="">All</option>
                      {filterOptions.shipment_types.map(st => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Priority</Label>
                    <select
                      value={filters.priority}
                      onChange={(e) => handleFilterChange("priority", e.target.value)}
                      className="w-full mt-1 text-sm border rounded-md px-3 py-2 bg-white"
                      data-testid="filter-priority"
                    >
                      <option value="">All</option>
                      {filterOptions.priority_types.map(pt => (
                        <option key={pt} value={pt}>{pt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Drop City</Label>
                    <select
                      value={filters.drop_city}
                      onChange={(e) => handleFilterChange("drop_city", e.target.value)}
                      className="w-full mt-1 text-sm border rounded-md px-3 py-2 bg-white"
                      data-testid="filter-drop-city"
                    >
                      <option value="">All Cities</option>
                      {filterOptions.cities.slice(0, 100).map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Drop Pincode</Label>
                    <Input
                      placeholder="Enter pincode"
                      value={filters.drop_pincode}
                      onChange={(e) => handleFilterChange("drop_pincode", e.target.value)}
                      className="mt-1"
                      data-testid="filter-drop-pincode"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Details Status</Label>
                    <select
                      value={filters.detail_fetched}
                      onChange={(e) => handleFilterChange("detail_fetched", e.target.value)}
                      className="w-full mt-1 text-sm border rounded-md px-3 py-2 bg-white"
                      data-testid="filter-detail-status"
                    >
                      <option value="">All</option>
                      <option value="true">Details Fetched</option>
                      <option value="false">Pending Details</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-teal-600 font-semibold">In Last Fetch</Label>
                    <select
                      value={filters.present_in_last_fetch}
                      onChange={(e) => handleFilterChange("present_in_last_fetch", e.target.value)}
                      className="w-full mt-1 text-sm border rounded-md px-3 py-2 bg-white"
                      data-testid="filter-present-in-last-fetch"
                    >
                      <option value="">All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-blue-600 font-semibold">OFD Count</Label>
                    <select
                      value={filters.ofd_count}
                      onChange={(e) => handleFilterChange("ofd_count", e.target.value)}
                      className="w-full mt-1 text-sm border rounded-md px-3 py-2 bg-white"
                      data-testid="filter-ofd-count"
                    >
                      <option value="">All</option>
                      {filterOptions.ofd_counts.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-red-600 font-semibold">Unattempted</Label>
                    <select
                      value={filters.unattempted_count}
                      onChange={(e) => handleFilterChange("unattempted_count", e.target.value)}
                      className="w-full mt-1 text-sm border rounded-md px-3 py-2 bg-white"
                      data-testid="filter-unattempted-count"
                    >
                      <option value="">All</option>
                      {filterOptions.unattempted_counts.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>

            {/* Table */}
            {loading ? (
              <div className="p-8 text-center text-slate-600">Loading shipments...</div>
            ) : shipments.length === 0 ? (
              <div className="p-12 text-center">
                <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <p className="text-lg font-medium text-slate-500 mb-1">
                  {searchQuery || activeFilterCount ? "No shipments match your filters" : "No shipments found"}
                </p>
                <p className="text-sm text-slate-400 mb-4">
                  {searchQuery || activeFilterCount ? "Try adjusting your filters" : 'Click "Fetch Orders" to sync from Pharmeasy'}
                </p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto mt-4">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">#</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Shipment ID</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Type</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Reference ID</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">LBN</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Package ID</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-orange-600 uppercase tracking-wider whitespace-nowrap">Pharmeasy Status</th>
                        <th className="px-3 py-3 text-right text-xs font-semibold text-blue-600 uppercase tracking-wider whitespace-nowrap">OFD</th>
                        <th className="px-3 py-3 text-right text-xs font-semibold text-red-600 uppercase tracking-wider whitespace-nowrap">Unatt.</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-purple-600 uppercase tracking-wider whitespace-nowrap">Edgistify Status</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Address</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Payment</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Pincode</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">City</th>
                        <th className="px-3 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap min-w-[120px]">EDD</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-teal-600 uppercase tracking-wider whitespace-nowrap min-w-[100px]">Last Fetched</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-teal-600 uppercase tracking-wider whitespace-nowrap">In Fetch</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Map</th>
                        <th className="px-3 py-3 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {shipments.map((s, idx) => (
                        <tr 
                          key={s._id} 
                          className={`${isEDDBreached(s) ? "bg-red-100 hover:bg-red-200 border-l-4 border-red-500" : "hover:bg-slate-50"}`}
                          data-testid={`shipment-row-${s._id}`}
                        >
                          <td className="px-3 py-3 align-middle text-sm text-slate-500">{(page - 1) * pageSize + idx + 1}</td>
                          <td className="px-3 py-3 align-middle text-sm font-mono font-medium text-slate-900">
                            <ClickableCell value={s.shipmentId} mongoId={s._id} className="font-medium" />
                          </td>
                          <td className="px-3 py-3 align-middle">
                            <Badge className={`text-xs ${s.type === "RETURN" ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                              {s.type || "—"}
                            </Badge>
                          </td>
                          <td className="px-3 py-3 align-middle text-sm font-mono text-slate-600">
                            <div className="flex items-center gap-1">
                              <span className="truncate max-w-[100px]" title={s.referenceId}>{s.referenceId || "—"}</span>
                              {s.referenceId && (
                                <button onClick={() => copyToClipboard(s.referenceId)} className="text-slate-400 hover:text-emerald-600" title="Copy Reference ID">
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 align-middle text-sm text-slate-600">
                            <ClickableCell value={s.lbn} mongoId={s._id} />
                          </td>
                          <td className="px-3 py-3 align-middle text-sm font-mono text-slate-500">
                            <ClickableCell value={s.packageId} mongoId={s._id} />
                          </td>
                          <td className="px-3 py-3 align-middle">
                            <Badge className={`text-xs ${getBoltStatusColor(s.bolt_status)}`}>
                              {s.bolt_status?.replace(/_/g, " ") || "—"}
                            </Badge>
                          </td>
                          <td className="px-3 py-3 align-middle text-right">
                            {s.ofd_count > 0 ? (
                              <span className={`text-sm font-medium ${s.ofd_count > 1 ? "text-red-600" : "text-blue-600"}`}>{s.ofd_count}</span>
                            ) : <span className="text-sm text-slate-300">0</span>}
                          </td>
                          <td className="px-3 py-3 align-middle text-right">
                            {s.unattempted_count > 0 ? (
                              <span className="text-sm font-medium text-red-600">{s.unattempted_count}</span>
                            ) : <span className="text-sm text-slate-300">0</span>}
                          </td>
                          <td className="px-3 py-3 align-middle">
                            <Badge className={`text-xs ${getOurStatusColor(s.our_status)}`}>
                              {s.our_status || "—"}
                            </Badge>
                          </td>
                          <td className="px-3 py-3 align-middle text-sm text-slate-600 max-w-[200px]">
                            <div className="flex items-center gap-1">
                              <span className="truncate" title={getFullAddress(s)}>{getFullAddress(s)}</span>
                              {getFullAddress(s) !== "—" && (
                                <button onClick={() => copyToClipboard(getFullAddress(s))} className="text-slate-400 hover:text-emerald-600 flex-shrink-0" title="Copy Address">
                                  <Copy className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3 align-middle">
                            <Badge className={s.paymentType === "COD"
                              ? "bg-orange-100 text-orange-800 text-xs"
                              : "bg-sky-100 text-sky-800 text-xs"}>
                              {s.paymentType || "—"}
                            </Badge>
                          </td>
                          <td className="px-3 py-3 align-middle text-sm font-mono text-slate-700">{getRelevantAddress(s)?.pincode || s.dropPincode || "—"}</td>
                          <td className="px-3 py-3 align-middle text-sm text-slate-600">{getRelevantAddress(s)?.city || s.dropCity || "—"}</td>
                          <td className="px-3 py-3 align-middle min-w-[120px]">
                            {s.committedExpectedDeliveryDate ? (
                              <div className={`whitespace-nowrap text-sm ${isEDDBreached(s) ? "text-red-700 font-medium" : "text-slate-600"}`}>
                                <div>{new Date(s.committedExpectedDeliveryDate).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                                <div className={`text-xs ${isEDDBreached(s) ? "text-red-600" : "text-slate-500"}`}>{new Date(s.committedExpectedDeliveryDate).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true })}</div>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 align-middle text-center min-w-[100px]">
                            {s.lastFetchedAt ? (
                              <div className="whitespace-nowrap text-sm text-slate-600">
                                <div>{new Date(s.lastFetchedAt).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                                <div className="text-xs text-slate-500">{new Date(s.lastFetchedAt).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 align-middle text-center">
                            {s.presentInLastFetch === true ? (
                              <Badge className="bg-teal-100 text-teal-800 text-xs">Yes</Badge>
                            ) : s.presentInLastFetch === false ? (
                              <Badge className="bg-red-100 text-red-700 text-xs">No</Badge>
                            ) : (
                              <span className="text-xs text-slate-400">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 align-middle text-center">
                            {getMapUrl(s) ? (
                              <a
                                href={getMapUrl(s)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center text-blue-600 hover:text-blue-700"
                                title="Open in Google Maps"
                              >
                                <MapPin className="w-4 h-4" />
                              </a>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-3 align-middle text-center">
                            <div className="flex items-center justify-center gap-1">
                            {!isTerminalStatus(s.bolt_status) && s.lbn ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleFetchEvents(s._id, s.lbn)}
                                disabled={fetchingEventsFor === s._id}
                                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 px-2 py-1 h-7 w-7"
                                title="Sync status from Pharmeasy"
                              >
                                <RefreshCw className={`w-3.5 h-3.5 ${fetchingEventsFor === s._id ? "animate-spin" : ""}`} />
                              </Button>
                            ) : (
                              <span className="text-xs text-slate-300">—</span>
                            )}
                            {(filters.status === "INWARD_PLANNED" || filters.status === "INWARDED" || (filters.status === "UNDELIVERED_RETRY" && s.our_status === "INWARD_PLANNED")) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleResetSingle(s.shipmentId)}
                                disabled={resettingSingle === s.shipmentId}
                                className="text-orange-600 border-orange-200 hover:bg-orange-50 hover:border-orange-300 px-2 py-1 h-7 w-7"
                                title="Reset to Unassigned"
                                data-testid={`reset-single-${s.shipmentId}`}
                              >
                                <RotateCcw className={`w-3.5 h-3.5 ${resettingSingle === s.shipmentId ? "animate-spin" : ""}`} />
                              </Button>
                            )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                    <p className="text-sm text-slate-500">
                      Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
                    </p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page-btn">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="flex items-center px-3 text-sm text-slate-600">Page {page} of {totalPages}</span>
                      <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="next-page-btn">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PharmeasyLayout>
  );
};

export default PharmeasyOrders;
